import { Component, computed, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ConfigurationWaterfall } from '../configuration-waterfall.service';
import { WorkspaceStore } from '../workspace-store';
import { DiffView } from '../diff-view/diff-view';
import { EnvironmentMeta } from '../environment-meta/environment-meta';
import {
  CrossItem, DiffLine, Environment, PayloadKind, TextFn,
  contentLabel as contentLabelFor, crossStatus as crossStatusFor, crossSymbol as crossSymbolFor,
  environmentLabel, environments, familyId, filterRows, hasData as hasDataFor, identity,
  payload, sideLines, specNames,
} from '../configuration-view';

@Component({
  selector: 'app-cross',
  imports: [
    FormsModule, MatButtonModule, MatCardModule, MatFormFieldModule, MatIconModule, MatInputModule,
    MatSelectModule, MatTableModule, TranslocoPipe, DiffView, EnvironmentMeta,
  ],
  templateUrl: './cross-page.html',
  styleUrl: './cross-page.scss',
})
export class CrossPage {
  private readonly transloco = inject(TranslocoService);
  private readonly workspace = inject(WorkspaceStore);
  readonly rows = this.workspace.rows;
  readonly loading = this.workspace.loading;
  readonly loaded = this.workspace.loaded;
  readonly busyLabel = this.workspace.busyLabel;
  readonly search = this.workspace.search;
  readonly renderMode = this.workspace.renderMode;
  readonly crossEnvironment = this.workspace.crossEnvironment;
  readonly leftSpec = this.workspace.leftSpec;
  readonly rightSpec = this.workspace.rightSpec;
  readonly crossSelection = this.workspace.crossSelection;

  readonly environments = environments;
  readonly crossColumns = ['configuration', 'leftSpec', 'pair', 'rightSpec'];
  readonly filtered = computed(() => filterRows(this.rows(), this.search()));
  readonly names = computed(() => specNames(this.rows()));
  readonly crossGrouped = computed<CrossItem[]>(() => {
    const leftName = this.leftSpec();
    const rightName = this.rightSpec();
    if (leftName === null || rightName === null || leftName === rightName) return [];
    const families = new Map<string, ConfigurationWaterfall[]>();
    for (const row of this.filtered()) {
      const list = families.get(familyId(row)) ?? [];
      list.push(row);
      families.set(familyId(row), list);
    }
    const pairs: Extract<CrossItem, { kind: 'row' }>[] = [];
    for (const list of families.values()) {
      if (new Set(list.map(row => row.swaggerName)).size < 2) continue;
      const sample = list[0];
      pairs.push({
        kind: 'row',
        key: sample.key,
        type: sample.type,
        organizationName: sample.organizationName,
        left: list.find(row => row.swaggerName === leftName) ?? null,
        right: list.find(row => row.swaggerName === rightName) ?? null,
      });
    }
    pairs.sort((a, b) => a.key.localeCompare(b.key, undefined, { sensitivity: 'base' })
      || a.type.localeCompare(b.type, undefined, { sensitivity: 'base' })
      || a.organizationName.localeCompare(b.organizationName, undefined, { sensitivity: 'base' }));
    const table: CrossItem[] = [];
    let current = '';
    let groupIndex = -1;
    for (const pair of pairs) {
      if (pair.key !== current) {
        current = pair.key;
        groupIndex = table.length;
        table.push({ kind: 'group', key: pair.key, count: 0 });
      }
      const group = table[groupIndex];
      if (group?.kind === 'group') group.count++;
      table.push(pair);
    }
    return table;
  });
  readonly isCrossGroup = (_index: number, item: CrossItem): boolean => item.kind === 'group';
  readonly isCrossRow = (_index: number, item: CrossItem): boolean => item.kind === 'row';
  private readonly text: TextFn = key => this.transloco.translate(key);

  label(environment: Environment): string {
    return environmentLabel(environment);
  }

  hasData(row: ConfigurationWaterfall, environment: Environment): boolean {
    return hasDataFor(row, environment);
  }

  contentLabel(row: ConfigurationWaterfall, environment: Environment): string {
    return contentLabelFor(row, environment, this.text);
  }

  crossCount(): number {
    return this.crossGrouped().filter(item => item.kind === 'row').length;
  }

  crossStatus(left: ConfigurationWaterfall | null, right: ConfigurationWaterfall | null): 'missing' | 'equal' | 'different' {
    return crossStatusFor(left, right, this.crossEnvironment());
  }

  crossSymbol(left: ConfigurationWaterfall | null, right: ConfigurationWaterfall | null): string {
    return crossSymbolFor(left, right, this.crossEnvironment(), this.renderMode(), this.transloco.getActiveLang(), this.text);
  }

  lines(kind: PayloadKind, side: 'left' | 'right'): DiffLine[] {
    const pair = this.crossSelection();
    if (!pair) return [];
    const environment = this.crossEnvironment();
    return sideLines(payload(pair.left, environment, kind), payload(pair.right, environment, kind), kind === 'ApiCore', side, this.renderMode(), this.text);
  }

  refresh(): void {
    this.workspace.refresh();
  }

  edit(): void {
    this.workspace.openEditor();
  }

  swapSpecs(): void {
    const left = this.leftSpec();
    this.leftSpec.set(this.rightSpec());
    this.rightSpec.set(left);
  }

  selectCross(item: CrossItem): void {
    if (item.kind !== 'row' || !item.left || !item.right) return;
    this.workspace.dismissOverlays();
    this.crossSelection.set({ left: item.left, right: item.right });
  }

  isCrossSelected(item: CrossItem): boolean {
    if (item.kind !== 'row' || !item.left || !item.right) return false;
    const selected = this.crossSelection();
    return selected !== null
      && identity(selected.left) === identity(item.left)
      && identity(selected.right) === identity(item.right);
  }
}
